import type { ICampaignVariableFallbacks } from '../../utils/campaignTemplateVariables';
import { renderCampaignTemplate } from '../../utils/campaignTemplateVariables';

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'canceled';

export interface ICampaignStats {
  sent: number;
  replies: number;
}

/** Field names match the Prospects filter fields — multi-value arrays for richer campaign targeting */
export interface ILeadPreferences {
  jobTitle: string[];
  countryCode: string[];
  city: string[];
  state: string[];
  school: string[];
  companyName: string[];
  companyIndustry: string[];
  companySize: string[];
}

export const EMPTY_LEAD_PREFERENCES: ILeadPreferences = {
  jobTitle: [],
  countryCode: [],
  city: [],
  state: [],
  school: [],
  companyName: [],
  companyIndustry: [],
  companySize: [],
};

export interface ISimulationConfig {
  enabled: boolean;
  intervalSeconds: number;   // how often the tick runs (default 60s, set lower for fast simulation)
  minLeadsPerCycle: number;  // N — min leads to advance per tick
  maxLeadsPerCycle: number;  // K — max leads to advance per tick
  replyProbability: number;  // 0–1, chance invite_sent → replied
  conversionProbability: number; // 0–1, chance replied → converted (else closed_lost)
}

export const DEFAULT_SIMULATION_CONFIG: ISimulationConfig = {
  enabled: false,
  intervalSeconds: 60,
  minLeadsPerCycle: 2,
  maxLeadsPerCycle: 5,
  replyProbability: 0.6,
  conversionProbability: 0.5,
};

export const COMPANY_SIZE_OPTIONS = [
  '1', '2-10', '11-50', '51-200', '201-500',
  '501-1000', '1001-5000', '5001-10000', '10001+',
];

export type CampaignLeadStatus = 'pending' | 'profile_visited' | 'post_liked' | 'post_commented' | 'invite_sent' | 'accepted' | 'message_sent' | 'email_sent' | 'skipped_not_open' | 'replied' | 'email_replied' | 'converted' | 'closed_lost' | 'failed' | 'suppressed';

/** Statuses after which no further sequence steps are executed */
export const TERMINAL_LEAD_STATUSES: CampaignLeadStatus[] = ['replied', 'email_replied', 'converted', 'closed_lost', 'failed', 'suppressed'];

/** Parked after open_only — eligible for Resend via InMail (not a hard failure). */
export const PARKED_LEAD_STATUSES: CampaignLeadStatus[] = ['skipped_not_open'];

/**
 * Final outcomes — shown grouped/apart from the engagement statuses in the funnel.
 * Also the statuses a user can pick by hand, so 'suppressed' is not one of them.
 */
export const OUTCOME_LEAD_STATUSES: CampaignLeadStatus[] = ['converted', 'closed_lost', 'failed'];

/**
 * Every status a lead ends in for good — the outcomes plus 'suppressed' (on the workspace
 * suppression list; only the list sets it). A campaign whose leads are all here is done.
 */
export const FINAL_LEAD_STATUSES: CampaignLeadStatus[] = [...OUTCOME_LEAD_STATUSES, 'suppressed'];

/** How an InMail / Open Profile step was delivered. */
export type CampaignMessageSendMethod = 'open_profile' | 'sn_inmail' | 'skipped_not_open';

export const SEND_METHOD_LABELS: Record<CampaignMessageSendMethod, string> = {
  open_profile: 'Open Profile',
  sn_inmail: 'InMail',
  skipped_not_open: 'Skipped (not Open)',
};

/**
 * Single source of truth for per-status colours (text/dot + soft background).
 * Deliberately muted to match the rest of the app — used by every status chip,
 * funnel and stat row so the palette stays consistent.
 */
export const STATUS_COLORS: Record<CampaignLeadStatus, { color: string; bg: string }> = {
  pending:           { color: '#9aa3b5', bg: '#f1f3f8' },
  profile_visited:   { color: '#7a6bb0', bg: '#f3f0fa' },
  post_liked:        { color: '#d56c84', bg: '#fbedf1' },
  post_commented:    { color: '#d2864f', bg: '#faf1e8' },
  invite_sent:       { color: '#cb9a50', bg: '#f9f3e7' },
  accepted:          { color: '#4f9e77', bg: '#eaf5ef' },
  message_sent:      { color: '#5286ad', bg: '#eaf2f8' },
  email_sent:        { color: '#4d8f9e', bg: '#e8f4f6' },
  skipped_not_open:  { color: '#9a7b4f', bg: '#f7f0e6' },
  replied:           { color: '#549d83', bg: '#e9f5f0' },
  email_replied:     { color: '#4e9b95', bg: '#e8f5f3' },
  converted:         { color: '#6d72cf', bg: '#eef0fb' },
  closed_lost:       { color: '#9aa3b5', bg: '#f1f3f8' },
  failed:            { color: '#cf6f6b', bg: '#fbecec' },
  suppressed:        { color: '#7d7f8f', bg: '#eeeef2' },
};

/**
 * Lead status names. A status is where the lead is NOW — leads move on as steps go
 * out — so each name describes a state ("Awaiting reply"), not an event ("Msg Sent"),
 * or people read the counts as totals and compare them with analytics.
 */
export const LEAD_STATUS_LABELS: Record<CampaignLeadStatus, string> = {
  pending: 'Not started',
  profile_visited: 'Profile visited',
  post_liked: 'Post liked',
  post_commented: 'Commented',
  invite_sent: 'Waiting to accept',
  accepted: 'Accepted, up next',
  message_sent: 'Awaiting reply',
  email_sent: 'Email awaiting reply',
  skipped_not_open: 'InMail not sent',
  replied: 'Replied',
  email_replied: 'Replied by email',
  converted: 'Converted',
  closed_lost: 'Closed lost',
  failed: 'Failed',
  suppressed: 'Suppressed',
};

/** One line on what a lead in each status is waiting for, shown on hover. */
export const LEAD_STATUS_HINTS: Record<CampaignLeadStatus, string> = {
  pending: 'Added to the campaign, first step not sent yet.',
  profile_visited: 'Profile visited, waiting for the next step.',
  post_liked: 'Post liked, waiting for the next step.',
  post_commented: 'Commented on a post, waiting for the next step.',
  invite_sent: 'Invite sent, not accepted yet.',
  accepted: 'Accepted the invite, next step not sent yet.',
  message_sent: 'Message or InMail sent, no reply yet.',
  email_sent: 'Email sent, no reply yet.',
  skipped_not_open: "Profile isn't Open, so the message wasn't sent. You can send them an InMail instead.",
  replied: 'Replied on LinkedIn. The sequence stopped for them.',
  email_replied: 'Replied by email. The sequence stopped for them.',
  converted: 'Marked as converted.',
  closed_lost: 'Marked as closed lost.',
  failed: "The sequence couldn't continue for this lead.",
  suppressed: "On a suppression list, so the campaign won't contact them.",
};

/** Fresh all-zero status counts object */
export const emptyLeadStatusCounts = (): ICampaignLeadStatusCounts => ({
  pending: 0, profile_visited: 0, post_liked: 0, post_commented: 0, invite_sent: 0, accepted: 0,
  message_sent: 0, email_sent: 0, skipped_not_open: 0, replied: 0, email_replied: 0,
  converted: 0, closed_lost: 0, failed: 0, suppressed: 0,
});

/** Shown when starting a campaign that has no leads yet — informational, not a blocker.
 *  An empty campaign runs idle and begins outreach as soon as leads are enrolled. */
export const CAMPAIGN_START_NO_LEADS_HINT = 'No leads yet — the campaign will start sending as soon as you add some.';

export function sumEnrolledLeads(counts?: ICampaignLeadStatusCounts | null): number {
  if (!counts) return 0;
  return Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0);
}

export type SequenceStepType = 'connection_request' | 'message' | 'sn_message' | 'profile_visit' | 'post_like' | 'post_comment' | 'email';

export type SnNonOpenFallback = 'open_only' | 'inmail_if_credits';

/** Which address an email step should use. */
export type EmailPreference = 'business' | 'private' | 'business_then_private';

/**
 * What an email step does when the lead has no usable address.
 *
 * There was a third option, 'enrich' ("look it up first"), which the executor never
 * acted on — it fell through to the same skip path, so the setting did nothing.
 * Looking an address up at send time means spending credits mid-run, which is a
 * deliberate decision rather than a checkbox, so the option is gone until that exists.
 * Stored 'enrich' values normalise to 'skip', which is what they already did.
 */
export type IfNoEmail = 'skip' | 'fail';

export interface ISequenceStep {
  type: SequenceStepType;
  /** Wait before this step, counted from the previous step (or the invite being accepted). */
  delayDays: number;
  /** Added to delayDays ("1 day 2h after the previous step"); absent when 0. */
  delayHours?: number;
  messageMode?: 'template' | 'ai';
  messageTemplate?: string;
  messageAiPrompt?: string;
  /** Required for sn_message (Open Profile / InMail). */
  messageSubject?: string;
  commentMode?: 'template' | 'ai';
  commentTemplate?: string;
  commentAiPrompt?: string;
  aiLanguage?: string;
  /** User-edited send text from preview — overrides AI/template at execution when set. */
  finalContent?: string;
  /** When true, connection_request step includes a personalized note. */
  includeNote?: boolean;
  /**
   * When free LinkedIn monthly note quota is exhausted:
   * without_note (default) | pause_noted
   * (pause_campaign kept in the type for legacy campaigns only)
   */
  noteLimitFallback?: 'without_note' | 'pause_noted' | 'pause_campaign';
  /**
   * sn_message when lead is not Open Profile:
   * open_only (default) | inmail_if_credits
   */
  snNonOpenFallback?: SnNonOpenFallback;

  // ── Email step (type === 'email') ──────────────────────────────────────────
  emailSubjectMode?: 'template' | 'ai';
  emailSubjectTemplate?: string;
  emailSubjectAiPrompt?: string;
  emailBodyMode?: 'template' | 'ai';
  emailBodyTemplate?: string;
  /** True when emailBodyTemplate holds rich-text markup written in the editor. */
  emailBodyHtml?: boolean;
  emailBodyAiPrompt?: string;
  emailPreference?: EmailPreference;
  ifNoEmail?: IfNoEmail;
  /** Follow-ups reply inside the existing thread rather than starting a new one. */
  replyInThread?: boolean;
}

/**
 * Hints are one short line each. They used to carry the full rationale, which made
 * every card three lines tall and turned a simple choice into a wall of prose.
 *
 * `cost` is kept separate rather than folded into the sentence: the credit price is
 * a number people compare across the three options, and it reads far faster in its
 * own column than buried mid-sentence. The running total still lives in the coverage
 * panel below, which is where the actual spend is decided.
 */
export const EMAIL_PREFERENCE_OPTIONS = [
  {
    value: 'business' as const,
    label: 'Work email only',
    hint: 'Use only work emails',
    cost: '1 credit',
  },
  {
    value: 'business_then_private' as const,
    label: 'Work email, then personal',
    hint: 'Try work email first, then personal if work is missing',
    cost: '1 or 3 credits',
  },
  {
    value: 'private' as const,
    label: 'Personal email only',
    hint: 'Use only personal emails',
    cost: '3 credits',
  },
];

export const IF_NO_EMAIL_OPTIONS = [
  {
    value: 'skip' as const,
    label: 'Skip this step',
    hint: 'Continue with the next step',
  },
  {
    value: 'fail' as const,
    label: 'Stop this lead',
    hint: 'Mark as failed and stop the sequence',
  },
];

export const NOTE_LIMIT_FALLBACK_OPTIONS = [
  {
    value: 'without_note' as const,
    label: 'Continue without notes',
    hint: 'Campaign keeps sending Connection Requests, but without a note, since you’ve hit your note limit (recommended).',
  },
  {
    value: 'pause_noted' as const,
    label: 'Pause invites with notes',
    hint: 'Leads that already passed the Connection Request step continue. The rest wait until your monthly quota resets (Not recommended).',
  },
];

export const SN_NON_OPEN_FALLBACK_OPTIONS = [
  {
    value: 'open_only' as const,
    label: 'Open Profile only',
    hint: 'Send messages only to leads with an Open Profile. Leads with a closed profile skip this step, and no InMail credits are used.',
  },
  {
    value: 'inmail_if_credits' as const,
    label: 'Use InMail credits',
    hint: 'Send messages to closed profiles using 1 InMail credit each. When credits run out, continue with free Open Profile messages.',
  },
];

/** Shown under the closed-profile choice when paid InMail is selected. */
export const SN_INMAIL_CREDITS_HOWTO =
  'How credits are used: LeadDelta first uses credits from the LinkedIn source the lead came from. If those credits are unavailable, it uses Sales Navigator credits, then Recruiter credits, when available.';

export const CONNECTION_REQUEST_NOTE_MAX_CHARS = 300;
export const CONNECTION_REQUEST_NOTES_MONTHLY_FREE = 5;
export const CONNECTION_REQUESTS_WEEKLY_HARD_CAP = 150;

export type CampaignLeadSource = 'leadsearch' | 'contacts' | 'csv' | 'posts' | 'sn_lists';

export interface ILeadMessage {
  stepType: SequenceStepType | 'retry' | 'status_change';
  content: string;
  subject?: string;
  sentAt: string;
  direction: 'outbound' | 'inbound' | 'system';
  /**
   * Which channel this activity happened on. Statuses stay channel-agnostic (an
   * email send yields 'message_sent' like a DM), so per-channel reporting reads
   * this field instead of inventing new statuses.
   */
  channel?: 'linkedin' | 'email';
  aiGenerated?: boolean;
  /** LinkedIn feed URL of the post that was liked or commented on. */
  postUrl?: string;
  sendMethod?: CampaignMessageSendMethod | '';
  creditsUsed?: number;
  conversationId?: string;
  /** connection_request only: the invite actually carried a note (false when it was dropped or skipped). */
  hasNote?: boolean;
  /**
   * A step that was deliberately not executed (e.g. the lead has no address).
   * Recorded rather than passed over silently so the funnel shows
   * "skipped — no address" instead of the step vanishing from the stats.
   */
  skipped?: boolean;
  skipReason?: string;
  restoredToStatus?: string;
  previousFailureReason?: string;
}

/** Per-lead customized send text for a specific sequence step. */
export interface ILeadStepContentOverride {
  stepIndex: number;
  content: string;
  updatedAt?: string;
}

export interface ICampaignLead {
  _id: string;
  campaignId: string;
  linkedinId: string;
  publicIdentifier: string;
  firstName: string;
  lastName: string;
  name: string;
  title: string;
  company: string;
  location: string;
  imageUrl: string;
  linkedinUrl: string;
  status: CampaignLeadStatus;
  source: CampaignLeadSource;
  enrolledAt: string;
  lastContactedAt: string | null;
  /** Proxy: last Unipile step attempt (success or failure) — used for campaign dailyLimit. */
  lastProcessedAt?: string | null;
  messageSentAt?: string | null;
  /** Last Open Profile / InMail channel for badge + analytics. */
  lastMessageSendMethod?: CampaignMessageSendMethod | '' | null;
  /**
   * Queued for paid InMail resend (same campaign). Cleared after cloud send.
   */
  forceInMailResend?: boolean;
  /** Non-terminal wait reason from cloud executor (limits, credits, …). */
  holdReason?: string;
  currentStepIndex?: number;
  nextStepAt?: string | null;
  failureReason?: string;
  statusBeforeFailure?: string;
  retryCounts?: Partial<Record<CampaignLeadStatus, number>>;
  failedAttempts?: number;
  messages?: ILeadMessage[];
  /** Per-lead message/comment overrides keyed by sequence step index. */
  stepContentOverrides?: ILeadStepContentOverride[];
  /** Proxy execution: the LinkedIn account assigned to this lead for its whole flow. */
  unipileAccountId?: string;
  /** Classic LinkedIn provider id (ACo…). */
  providerId?: string;
  /** Sales Navigator provider id (ACw…) for InMail reply webhooks. */
  salesNavProviderId?: string;
  /**
   * The sender identity (LinkedIn + mailbox, paired) assigned to this lead on its
   * first step and fixed for life — so every channel reaches them as one person.
   */
  identityId?: string | null;
  email?: string;
  emailType?: '' | 'business' | 'private';
  emailSource?: '' | 'csv' | 'contacts' | 'enrichment' | 'manual';
  emailStatus?: 'unknown' | 'ok' | 'bounced' | 'suppressed';
  lastEmailSentAt?: string | null;
  /** Employer's domain, where the source carried one — matched by domain suppressions. */
  companyDomain?: string;
  /** Set with status 'suppressed': which suppression-list entry took the lead out. */
  suppression?: {
    entryId?: string | null;
    listId?: string | null;
    /** The list the entry was on, as named when it matched. */
    listName?: string;
    /** The reason given when the entry was added, e.g. "existing customer". */
    note?: string;
    type?: SuppressionType | '';
    value?: string;
    /** company_name: a domain entry matched through its company name. */
    matchedBy?: 'profile' | 'email' | 'domain' | 'company_name' | '';
    at?: string | null;
    previousStatus?: string;
  };
}

// ─── Suppression lists ───────────────────────────────────────────────────────

export type SuppressionType = 'profile' | 'domain' | 'email';

/**
 * Which campaigns a list binds:
 *  - all      — every campaign in the workspace (the workspace list is one)
 *  - others   — every campaign except its owner's: contacts the owner keeps for themselves
 *  - selected — only campaigns that pick it in their own settings
 */
export type SuppressionListScope = 'all' | 'others' | 'selected';

/** One list of GET /campaigns/suppression-lists. */
export interface ISuppressionList {
  _id: string;
  name: string;
  scope: SuppressionListScope;
  /** The workspace list: always 'all', cannot be renamed or deleted. */
  isDefault: boolean;
  owner: { _id: string; name: string } | null;
  /** Entries, plus the email channel's rows on the workspace list. */
  entryCount: number;
  createdAt?: string;
  /** What the viewer may do — the API enforces the same rules. */
  canAdd: boolean;
  canManage: boolean;
}

export interface ISuppressionListsResponse {
  lists: ISuppressionList[];
  userId: string;
  isAdmin: boolean;
}

/**
 * One row of GET /campaigns/suppressions. On the workspace list two lists are shown as one:
 *  - list 'contact' — the suppression list proper; scope 'all', every channel
 *  - list 'email'   — the email channel's own list (unsubscribes, bounces); scope 'email'
 */
export interface ISuppressionEntry {
  _id: string;
  list: 'contact' | 'email';
  scope: 'all' | 'email';
  type: SuppressionType;
  value: string;
  /** contact rows: 'manual'. email rows: unsubscribe | hard_bounce | complaint | manual | invalid */
  reason: string;
  source?: 'manual' | 'lead' | 'api';
  note?: string;
  matchCompanyName?: boolean;
  companyName?: string;
  leadsRemoved?: number;
  createdBy?: { _id: string; name: string } | null;
  createdAt: string;
  /**
   * Whether this viewer may remove the row: an admin, the list's owner or whoever added
   * it. Unsubscribes and complaints are the recipient's own decision and never are.
   */
  removable: boolean;
}

export interface ISuppressionListResponse {
  suppressions: ISuppressionEntry[];
  total: number;
  counts: Record<SuppressionType, number>;
  list: Pick<ISuppressionList, '_id' | 'name' | 'scope' | 'isDefault'>;
  /** Whether this viewer may add to the list. */
  canAdd: boolean;
}

export interface ISuppressionPreview {
  listId: string;
  listName: string;
  valid: number;
  alreadyListed: number;
  invalid: { value: string; reason: string }[];
  leadsAffected: number;
  campaignsAffected: number;
}

export interface ISuppressionAddResult {
  added: number;
  alreadyListed: number;
  invalid: { value: string; reason: string }[];
  leadsRemoved: number;
  campaignsAffected: number;
}

export interface ICampaignLeadStatusCounts {
  pending: number;
  profile_visited: number;
  post_liked: number;
  post_commented: number;
  invite_sent: number;
  accepted: number;
  message_sent: number;
  email_sent: number;
  skipped_not_open: number;
  replied: number;
  email_replied: number;
  converted: number;
  closed_lost: number;
  failed: number;
  suppressed: number;
}

export interface ICampaignMessagingStats {
  openProfile: number;
  snInmail: number;
  skippedNotOpen: number;
}

export const emptyMessagingStats = (): ICampaignMessagingStats => ({
  openProfile: 0,
  snInmail: 0,
  skippedNotOpen: 0,
});

export interface IMessageConfig {
  mode: 'none' | 'template' | 'ai';
  template: string;
  aiPrompt: string;
}

export type CampaignExecutionMethod = 'extension' | 'proxy';

/** What the backend returns on a campaign's unipileConfig field */
export interface IUnipileConfig {
  accountIds: string[];
  /** Accounts awaiting 2FA checkpoint verification — not yet in the active pool */
  pendingAccountIds?: string[];
  linkedInAccountsConfigured?: boolean;
}

export interface ISendingWindow {
  enabled:   boolean;
  timezone:  string;
  days:      string[];  // ['Mon','Tue','Wed','Thu','Fri']
  startHour: number;
  endHour:   number;
}

/**
 * New campaigns open on "Restrict to schedule" (Mon–Fri, 09:00–17:00) rather than
 * running 24/7 — sending inside business hours is the safer starting point.
 * Timezone falls back to UTC here; the create form overwrites it with the browser
 * timezone when one is available. Campaigns saved earlier keep whatever they stored:
 * the edit path spreads the persisted window over this default.
 */
export const DEFAULT_SENDING_WINDOW: ISendingWindow = {
  enabled:   true,
  timezone:  'UTC',
  days:      ['Mon','Tue','Wed','Thu','Fri'],
  startHour: 9,
  endHour:   17,
};

export const DAY_OPTIONS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

/**
 * Countries with available proxy IPs for LinkedIn account connections.
 * Source: https://developer.unipile.com/docs/connect-accounts ("Available IP Countries")
 */
export const PROXY_COUNTRY_OPTIONS: { value: string; label: string }[] = [
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'AO', label: 'Angola' },
  { value: 'AR', label: 'Argentina' },
  { value: 'AT', label: 'Austria' },
  { value: 'AU', label: 'Australia' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'BE', label: 'Belgium' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'BR', label: 'Brazil' },
  { value: 'CA', label: 'Canada' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'CL', label: 'Chile' },
  { value: 'CN', label: 'China' },
  { value: 'CO', label: 'Colombia' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'CZ', label: 'Czechia' },
  { value: 'DE', label: 'Germany' },
  { value: 'DK', label: 'Denmark' },
  { value: 'EE', label: 'Estonia' },
  { value: 'EG', label: 'Egypt' },
  { value: 'ES', label: 'Spain' },
  { value: 'FI', label: 'Finland' },
  { value: 'FR', label: 'France' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IL', label: 'Israel' },
  { value: 'IN', label: 'India' },
  { value: 'IT', label: 'Italy' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'KZ', label: 'Kazakhstan' },
  { value: 'MA', label: 'Morocco' },
  { value: 'MX', label: 'Mexico' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'NO', label: 'Norway' },
  { value: 'PA', label: 'Panama' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'PL', label: 'Poland' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'PT', label: 'Portugal' },
  { value: 'RO', label: 'Romania' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'SE', label: 'Sweden' },
  { value: 'SG', label: 'Singapore' },
  { value: 'TH', label: 'Thailand' },
  { value: 'TN', label: 'Tunisia' },
  { value: 'TR', label: 'Turkey' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'US', label: 'United States' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'ZA', label: 'South Africa' },
].sort((a, b) => a.label.localeCompare(b.label));

/** How a LinkedIn account is signed in. Credentials open a new session; cookies reuse the browser's. */
export type LinkedInConnectMethod = 'credentials' | 'cookies';

/**
 * What the connect calls send for the browser session already open on LinkedIn.
 * Works for classic LinkedIn, Sales Navigator, and Recruiter.
 * `accessToken` is the `li_at` cookie. `premiumAccessToken` is `li_a`, sent only
 * when the account uses Sales Navigator or Recruiter.
 * The API forwards them to Unipile with `userAgent` from this browser.
 */
export interface ILinkedInCookieLogin {
  accessToken: string;
  premiumAccessToken?: string;
  userAgent: string;
}

/** Email and password. Unipile opens its own LinkedIn session. */
export interface ILinkedInCredentialLogin {
  liEmail: string;
  liPassword: string;
}

export type ILinkedInLoginFields = ILinkedInCredentialLogin | ILinkedInCookieLogin;

/** Form-side state for connecting a LinkedIn account through Unipile */
export interface IUnipileConnectForm {
  method:     LinkedInConnectMethod;
  country:    string;
  liEmail:    string;
  liPassword: string;
  /** `li_at` from the browser where this account is already signed in. */
  liAt:       string;
  /** `li_a` from that same browser. Needed for Sales Navigator and Recruiter. */
  liA:        string;
  linkedInAccountConfigured?: boolean;
}

export const DEFAULT_UNIPILE_CONNECT_FORM: IUnipileConnectForm = {
  method:     'credentials',
  country:    'US',
  liEmail:    '',
  liPassword: '',
  liAt:       '',
  liA:        '',
};

/** The login half of a connect payload. Callers still attach `country` the way they already do. */
export function linkedInLoginFields(form: IUnipileConnectForm): ILinkedInLoginFields {
  if (form.method === 'cookies') {
    const premiumAccessToken = form.liA.trim();
    return {
      accessToken: form.liAt.trim(),
      ...(premiumAccessToken ? { premiumAccessToken } : {}),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };
  }
  return {
    liEmail: form.liEmail.trim(),
    liPassword: form.liPassword,
  };
}

/**
 * Where a campaign's recipients come from — mirrors CAMPAIGN_LEAD_SOURCES on the API.
 * Superset of CampaignLeadSource (the per-lead origin): 'empty' is a campaign-level
 * choice ("add recipients later") and never lands on an individual lead.
 */
export type CampaignRecipientSource = CampaignLeadSource | 'empty';

export interface ICampaign {
  _id: string;
  name: string;
  description?: string;
  status: CampaignStatus;
  stats: ICampaignStats;
  runTimer?: {
    running: boolean;
    startedAt: string | null;
    elapsedMs: number;
  };
  statusCounts?: ICampaignLeadStatusCounts;
  messagingStats?: ICampaignMessagingStats;
  sequence?: ISequenceStep[];
  messageConfig?: IMessageConfig;
  leadPreferences?: ILeadPreferences;
  /** Fallback text when a {{variable}} has no lead value at send time. */
  variableFallbacks?: ICampaignVariableFallbacks;
  leadLimit: number;
  /** Persisted recipient source — absent on campaigns created before it was stored. */
  leadSource?: CampaignRecipientSource | null;
  simulationConfig?: ISimulationConfig;
  executionMethod?: CampaignExecutionMethod;
  unipileConfig?: IUnipileConfig;
  /** Sender identities this campaign draws from (round-robin, sticky per lead). */
  identityIds?: string[];
  /** Opt-in suppression lists (scope 'selected') this campaign applies. */
  suppressionListIds?: string[];
  emailConfig?: ICampaignEmailConfig;
  sendingWindow?: ISendingWindow;
  dailyLimit?: number;
  lastFetchToken?: string | null;
  lastFetchedAt?: string | null;
  workspaceId: string;
  createdBy: string;
  /** Display name of the user who created the campaign (populated by the API). */
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Sender identities (LinkedIn + mailbox, paired) ──────────────────────────

/**
 * Email signature, as structured fields rather than free HTML.
 *
 * The markup is generated server-side from these values, so every signature looks
 * consistent and nothing typed here can break the message or inject markup into a
 * recipient's inbox. The logo is uploaded to our own bucket, never hotlinked.
 */
/**
 * The sender's own signature markup, pasted from their mail client. Stored already
 * sanitized by the backend, with inline base64 images rehosted to our bucket.
 */
export interface ISenderSignature {
  enabled: boolean;
  html: string;
}

/**
 * One person sending outreach. A lead is assigned an identity — never a bare
 * account — so the invite, the DM and the email all arrive from the same human.
 *
 * `linkedin.ready` and `emailReady` are independent by design: a mailbox that needs
 * reconnecting must never report the LinkedIn half as broken, and vice versa.
 */
export interface ISenderIdentity {
  _id: string;
  name: string;
  /** User who connected this sender. */
  createdBy?: string | null;
  /** This sender is the viewer's own LinkedIn profile — labelled "(Me)". */
  isMe?: boolean;
  /**
   * Offered when a NEW campaign is built. Campaigns already running keep using this
   * sender regardless — the send path does not read it.
   */
  active?: boolean;
  /** Live campaigns this sender is used by. Only returned by the workspace listing. */
  campaigns?: { _id: string; name: string; status: string }[];
  linkedin: {
    kind: 'extension' | 'unipile';
    profileId: string;
    accountId: string;
    name: string;
    /**
     * The LinkedIn login email the account was connected with, from the workspace
     * mapping — Unipile never returns it. Only filled by the workspace listing; empty
     * when the account was never connected through this workspace.
     */
    email?: string;
    ready: boolean;
    /**
     * Live provider status — 'connected' | 'credentials' | 'not_found' | 'error'.
     * Only filled by the workspace listing when asked for live data; a locally stored
     * accountId alone says nothing about whether the account still exists.
     */
    status?: string;
    /** Paid LinkedIn products on the session (live listing only): 'premium' | 'sales_navigator' | 'recruiter'. */
    premiumFeatures?: string[];
    /** InMail balances — only when the workspace listing is asked for credits. */
    inMailCredits?: {
      classic: number | null;
      salesNavigator: number | null;
      recruiter?: number | null;
      error?: string | null;
    } | null;
  };
  mail: {
    connected: boolean;
    address: string;
    fromName: string;
    provider: string;
    status: string;
    pausedAt: string | null;
    pauseReason: string;
    /** Set when the From-name and the LinkedIn name look like different people. */
    nameMismatchWarning: string;
    warmupStartedAt: string | null;
    /** Today's allowance — the ramp's figure, or the user's own if they set one. */
    dailyCap: number;
    sentToday: number;
    remainingToday: number;
    /** 0 = following the warmup ramp. */
    dailyLimitOverride: number;
    /** What the ramp would allow today — shown so raising it is an informed choice. */
    warmupSuggested: number;
    /** Ceiling nobody may cross. */
    hardCap: number;
    /** The ramp itself, so the UI can state it instead of saying "it goes up". */
    warmupStart?: number;
    warmupIncrement?: number;
    bounceStats: { sent: number; hardBounces: number };
    /** SPF/DKIM/DMARC as of the last connect — advisory, never blocking. */
    domainCheck: {
      checkedAt: string | null;
      domain: string;
      freeProvider: boolean;
      spfOk: boolean;
      dmarcOk: boolean;
      dkimOk: boolean;
      warnings: string[];
    };
  };
  /** Sign-off appended to every email this sender sends, across all campaigns. */
  signature: ISenderSignature;
  emailReady: boolean;
  emailBlockedReason: string | null;
}

/**
 * A mailbox this workspace already connected, offered for reuse — the mailbox
 * counterpart of IWorkspaceAccount on the LinkedIn side.
 */
export interface IAvailableMailbox {
  accountId: string;
  address: string;
  name: string | null;
  provider: string;
  /** connected / credentials / not_found / error */
  status: string;
  /** Sender currently holding it, or null when free. */
  usedBy: string | null;
  inThisIdentity: boolean;
}

/** Pre-activation check — can every sender actually run this sequence? */
export interface ICampaignEmailValidation {
  ok: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Address coverage for a campaign's recipients.
 *
 * Built from what we already hold — no provider lookups just to draw this. That means
 * `estimatedCredits` is an upper bound (every remaining lead priced as if an address
 * exists); the real figure comes back from the run, which only charges for hits.
 */
export interface IEmailPreflight {
  total: number;
  haveAddress: number;
  /** Address-less leads that could be looked up. */
  remaining: number;
  /**
   * Leads with no LinkedIn URL yet — Sales Navigator imports carry only an ACw id
   * until their vanity is resolved, and every lookup needs the URL. Reported so the
   * three numbers still add up to the total instead of quietly losing rows.
   */
  awaitingIdentifier: number;
  suppressed: number;
  blockedByEnrichmentOptOut: number;
  /** Upper bound — the run charges only for addresses actually found. */
  estimatedCredits: number;
  preference: EmailPreference;
  /** Present on POST responses. */
  started?: boolean;
  requiresConfirmation?: boolean;
  enrichedTransactionId?: string;
  /** Leads enriched inline (LeadSearch/CSV ones) — already on the leads. */
  enrichedNow?: number;
  creditsUsed?: number;
  /** True when the run stopped because the credit balance ran out. */
  outOfCredits?: boolean;
}

export interface ICreateCampaignPayload {
  name: string;
  description?: string;
  executionMethod?: CampaignExecutionMethod;
  unipileAccountIds?: string[];
}

export interface IUpdateCampaignPayload {
  name?: string;
  description?: string;
  status?: CampaignStatus;
  executionMethod?: CampaignExecutionMethod;
  leadPreferences?: ILeadPreferences;
  leadLimit?: number;
  leadSource?: CampaignRecipientSource | null;
  dailyLimit?: number;
  simulationConfig?: Partial<ISimulationConfig>;
  messageConfig?: Partial<IMessageConfig>;
  sequence?: ISequenceStep[];
  sendingWindow?: ISendingWindow;
  variableFallbacks?: ICampaignVariableFallbacks;
  emailConfig?: Partial<ICampaignEmailConfig>;
}

/**
 * Campaign-level email settings. The sender (mailbox, from-name, signature) comes
 * from the assigned identity — only the compliance footer lives here, because a cold
 * email legally needs an opt-out and a postal address where a LinkedIn DM does not.
 */
export interface ICampaignEmailConfig {
  physicalAddress?: string;
  footer?: string;
  preflight?: {
    runAt: string | null;
    creditsSpent: number;
    leadsEnriched: number;
  };
}

export interface IExecuteStepPayload {
  stepType: SequenceStepType;
  content: string;
  currentStepIndex: number;
}

/**
 * Sequence step type → status the lead gets after that step succeeds.
 * 'email' maps to the same status as a LinkedIn DM on purpose: statuses describe
 * where the lead is in the funnel, not which channel got it there. The channel
 * lives on messages[].channel, which is why adding email left the funnel untouched.
 */
export const STEP_STATUS: Record<SequenceStepType, CampaignLeadStatus> = {
  connection_request: 'invite_sent',
  message:            'message_sent',
  sn_message:         'message_sent',
  profile_visit:      'profile_visited',
  post_like:          'post_liked',
  post_comment:       'post_commented',
  email:              'email_sent',
};

/**
 * Status a lead must have before sequence step `stepIdx` can execute.
 * When a message step follows a connection_request, the lead must be `accepted`
 * (invite accepted on LinkedIn) — not merely `invite_sent`.
 */
export function expectedStatusForStep(stepIdx: number, sequence: ISequenceStep[]): CampaignLeadStatus | 'pending' {
  if (stepIdx === 0) return 'pending';
  const step = sequence[stepIdx];
  if (step?.type === 'message' && sequence.slice(0, stepIdx).some(s => s.type === 'connection_request')) {
    return 'accepted';
  }
  return STEP_STATUS[sequence[stepIdx - 1]?.type] ?? 'pending';
}

/** Campaign sequence used for funnel/stepper when the stored array is empty. */
export function resolveCampaignSequence(sequence?: ISequenceStep[] | null): ISequenceStep[] {
  return sequence?.length ? sequence : DEFAULT_SEQUENCE;
}

/**
 * Which reply stage a step can produce, if any. Used to place each channel's reply
 * directly after its own send in the funnel, rather than pooling every send and then
 * every reply — that ordering makes a two-channel campaign hard to read.
 */
export const REPLY_STATUS_FOR_STEP: Partial<Record<SequenceStepType, CampaignLeadStatus>> = {
  message: 'replied',
  sn_message: 'replied',
  email:   'email_replied',
};

/**
 * Has a LinkedIn DM / InMail step. Use this ONLY for LinkedIn-specific behaviour, such as the
 * extension polling LinkedIn chats for a reply — an email reply never arrives there.
 */
export function sequenceHasMessageStep(sequence?: ISequenceStep[] | null): boolean {
  return resolveCampaignSequence(sequence).some(s => s.type === 'message' || s.type === 'sn_message');
}

/**
 * Can a lead in this campaign end up 'replied'? True for a LinkedIn DM / InMail **or** an
 * email step — an emailed prospect replies just the same, and the webhook sets the
 * very same status.
 *
 * This is what the funnel should ask. Asking sequenceHasMessageStep instead hid the
 * Replied stage entirely from email-only campaigns, even while replies were being
 * recorded against those leads.
 */
export function sequenceExpectsReply(sequence?: ISequenceStep[] | null): boolean {
  return resolveCampaignSequence(sequence).some(s => s.type === 'message' || s.type === 'sn_message' || s.type === 'email');
}

const MESSAGE_REPLY_DONE: CampaignLeadStatus[] = ['replied', 'converted', 'closed_lost', 'failed', 'suppressed', 'skipped_not_open'];

const LINKEDIN_MESSAGING_STEP_TYPES = new Set(['message', 'sn_message']);

type LeadMessageFlags = { direction?: string; stepType?: string; skipped?: boolean; sendMethod?: string; hasNote?: boolean };

function isOutboundLinkedInMessaging(m: LeadMessageFlags): boolean {
  return m.direction === 'outbound'
    && LINKEDIN_MESSAGING_STEP_TYPES.has(String(m.stepType || ''))
    && !m.skipped
    && m.sendMethod !== 'skipped_not_open';
}

/** Connection request that actually carried a note — the note opens the thread like a DM. */
export function isOutboundConnectionNote(m: LeadMessageFlags): boolean {
  return m.direction === 'outbound'
    && m.stepType === 'connection_request'
    && m.hasNote === true
    && !m.skipped;
}

/** Anything we sent that the lead can answer in the LinkedIn thread (DM, InMail, invite note). */
export function isOutboundLinkedInOpener(m: LeadMessageFlags): boolean {
  return isOutboundLinkedInMessaging(m) || isOutboundConnectionNote(m);
}

/** The lead's own message — acceptance is logged as an inbound connection_request row too. */
function isInboundLinkedInReply(m: LeadMessageFlags): boolean {
  return m.direction === 'inbound' && m.stepType !== 'connection_request';
}

/** Sequence has an invite that may carry a note. */
function sequenceHasConnectionNote(sequence?: ISequenceStep[] | null): boolean {
  return resolveCampaignSequence(sequence).some(s => s.type === 'connection_request' && isTextContentStep(s));
}

/**
 * We sent the lead something on LinkedIn they can answer — a campaign DM (classic or
 * sn_message InMail/Open Profile) or a connection-request note — and we are still
 * waiting for an inbound reply.
 * Applies even when later sequence steps (post like/comment) already ran.
 * Mirrors backend leadIsAwaitingMessageReply, except that a noted invite still
 * pending acceptance is not polled here (no thread yet; every poll costs LinkedIn calls).
 */
export function isAwaitingCampaignMessageReply(
  lead: Pick<ICampaignLead, 'status' | 'currentStepIndex' | 'messages' | 'messageSentAt'>,
  sequence?: ISequenceStep[] | null,
): boolean {
  if (MESSAGE_REPLY_DONE.includes(lead.status)) return false;
  if ((lead.messages ?? []).some(isInboundLinkedInReply)) return false;
  if ((lead.messages ?? []).some(isOutboundConnectionNote)) return lead.status !== 'invite_sent';
  if (!sequenceHasMessageStep(sequence)) return false;

  const seq = resolveCampaignSequence(sequence);
  const firstMsgIdx = seq.findIndex(s => LINKEDIN_MESSAGING_STEP_TYPES.has(s.type));
  if (firstMsgIdx < 0) return false;

  const stepIdx = lead.currentStepIndex ?? 0;
  if (stepIdx <= firstMsgIdx) return false;

  const sentMessaging = (lead.messages ?? []).some(isOutboundLinkedInMessaging);
  if (!sentMessaging && !lead.messageSentAt) return false;

  return true;
}

/** Campaign still needs reply polling (e.g. paused after post steps, no message_sent left in counts). */
export function campaignMayHaveAwaitingReplies(
  sequence: ISequenceStep[] | undefined | null,
  statusCounts?: ICampaignLeadStatusCounts | null,
): boolean {
  if (!statusCounts) return false;
  const c = statusCounts;
  // Accepted leads whose invite carried a note can already be replying in that thread.
  if (sequenceHasConnectionNote(sequence) && (c.accepted ?? 0) > 0) return true;
  if (!sequenceHasMessageStep(sequence)) return false;
  return (c.message_sent ?? 0) > 0
    || (c.profile_visited ?? 0) > 0
    || (c.post_liked ?? 0) > 0
    || (c.post_commented ?? 0) > 0;
}

/** Cumulative funnel count — leads who reached a status and may have moved on (e.g. replied). */
export function cumulativeStatusCount(
  counts: ICampaignLeadStatusCounts,
  status: CampaignLeadStatus,
): number {
  const n = (s: CampaignLeadStatus) => counts[s] ?? 0;
  switch (status) {
    case 'message_sent':
      return n('message_sent') + n('replied') + n('converted');
    case 'invite_sent':
      return n('invite_sent') + n('accepted') + n('message_sent') + n('replied') + n('converted');
    case 'profile_visited':
      return n('profile_visited') + n('post_liked') + n('post_commented') + n('invite_sent') + n('accepted')
        + n('message_sent') + n('replied') + n('converted');
    case 'post_liked':
      return n('post_liked') + n('post_commented') + n('invite_sent') + n('accepted')
        + n('message_sent') + n('replied') + n('converted');
    case 'post_commented':
      return n('post_commented') + n('invite_sent') + n('accepted')
        + n('message_sent') + n('replied') + n('converted');
    default:
      return n(status);
  }
}

/** Steps that carry user-visible text (message, comment, or connection note). */
export function isTextContentStep(step: ISequenceStep): boolean {
  if (step.type === 'message' || step.type === 'post_comment' || step.type === 'email') return true;
  if (step.type === 'connection_request') {
    return !!(step.includeNote || step.finalContent?.trim() || step.messageTemplate?.trim() || step.messageAiPrompt?.trim());
  }
  return false;
}

/**
 * Is this email step ready to run? A body is always required; a subject is required
 * only for the first email — a follow-up replying in-thread inherits the original.
 */
export function emailStepIsIncomplete(step: ISequenceStep, isFirstEmailStep: boolean): boolean {
  if (step.type !== 'email') return false;

  const body = step.emailBodyMode === 'ai' ? step.emailBodyAiPrompt : step.emailBodyTemplate;
  if (!body?.trim() && !step.finalContent?.trim()) return true;

  const needsSubject = isFirstEmailStep || step.replyInThread === false;
  if (!needsSubject) return false;

  const subject = step.emailSubjectMode === 'ai' ? step.emailSubjectAiPrompt : step.emailSubjectTemplate;
  return !subject?.trim();
}

export function getLeadStepContentOverride(
  lead: Pick<ICampaignLead, 'stepContentOverrides'>,
  stepIndex: number,
): string | undefined {
  return lead.stepContentOverrides?.find(o => o.stepIndex === stepIndex)?.content;
}

export function leadHasStepContentOverride(
  lead: Pick<ICampaignLead, 'stepContentOverrides'>,
  stepIndex: number,
): boolean {
  return !!getLeadStepContentOverride(lead, stepIndex)?.trim();
}

/** Next text step this lead can customize (current or upcoming in sequence). */
export function getUpcomingTextStep(
  lead: Pick<ICampaignLead, 'currentStepIndex' | 'status'>,
  sequence: ISequenceStep[],
): { stepIndex: number; step: ISequenceStep } | null {
  if (lead.status === 'converted' || lead.status === 'closed_lost' || lead.status === 'suppressed') return null;
  const seq = resolveCampaignSequence(sequence);
  const startIdx = lead.currentStepIndex ?? 0;
  for (let i = startIdx; i < seq.length; i++) {
    const step = seq[i];
    if (!isTextContentStep(step)) continue;
    if (i < startIdx) continue;
    return { stepIndex: i, step };
  }
  return null;
}

export type {
  CampaignVariableKey,
  ICampaignVariableFallbacks,
} from '../../utils/campaignTemplateVariables';

export {
  CAMPAIGN_TEMPLATE_VARIABLES,
  DEFAULT_CAMPAIGN_VARIABLE_FALLBACKS,
  collectSequenceVariables,
  extractVariablesFromText,
  getMissingVariableFallbacks,
  getSequenceMissingFallbacks,
  mergeVariableFallbacks,
  normalizeVariableFallbacks,
  renderCampaignTemplate,
  variableLabel,
  variableToken,
} from '../../utils/campaignTemplateVariables';

/** True when the step's text must be generated per lead at send time rather than
 *  frozen at build time. An AI comment has to respond to the post THIS lead
 *  published, so step.finalContent (the builder preview) never applies to it —
 *  otherwise every lead receives the identical comment. */
export function isPerLeadAiComment(step: Pick<ISequenceStep, 'type' | 'commentMode'>): boolean {
  return step.type === 'post_comment' && step.commentMode === 'ai';
}

/** Campaign default send text for one lead (no per-lead override). */
export function resolveDefaultStepContent(
  step: ISequenceStep,
  lead: Pick<ICampaignLead, 'firstName' | 'lastName' | 'name' | 'company' | 'title'>,
  senderName = '',
  fallbacks: ICampaignVariableFallbacks = {},
): string {
  if (step.type === 'message') {
    if (step.finalContent?.trim()) return renderCampaignTemplate(step.finalContent, lead, senderName, fallbacks);
    const tpl = step.messageMode === 'template' ? step.messageTemplate : step.messageAiPrompt;
    return renderCampaignTemplate(tpl ?? '', lead, senderName, fallbacks);
  }
  if (step.type === 'post_comment') {
    if (!isPerLeadAiComment(step) && step.finalContent?.trim()) {
      return renderCampaignTemplate(step.finalContent, lead, senderName, fallbacks);
    }
    const tpl = step.commentMode === 'template' ? step.commentTemplate : step.commentAiPrompt;
    return renderCampaignTemplate(tpl ?? '', lead, senderName, fallbacks);
  }
  if (step.type === 'connection_request') {
    if (!step.includeNote && !step.finalContent?.trim() && !step.messageTemplate?.trim() && !step.messageAiPrompt?.trim()) {
      return '';
    }
    if (step.finalContent?.trim()) return renderCampaignTemplate(step.finalContent, lead, senderName, fallbacks);
    const tpl = step.messageMode === 'template' ? step.messageTemplate : step.messageAiPrompt;
    return renderCampaignTemplate(tpl ?? '', lead, senderName, fallbacks);
  }
  return '';
}

export const DEFAULT_AI_LANGUAGE = 'Auto';

export const DEFAULT_SEQUENCE: ISequenceStep[] = [
  { type: 'connection_request', delayDays: 0 },
  { type: 'message', delayDays: 1, messageMode: 'ai', messageTemplate: '', messageAiPrompt: '', aiLanguage: DEFAULT_AI_LANGUAGE },
];
